import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Contact } from 'lucide-react';
import {
  type AddExternalContactRequest,
  type ExternalContact,
  type PopulatedExternalContact
} from '@bliks-io/contracts';
import {
  EntityPickerHighlightedText,
  ExternalContactCard,
  ExternalContactFormDialog,
  type EntityPickerConfig,
  type EntityPickerRowMeta,
  type EntityPickerSearchKey,
  type EntityPickerSuggestNew
} from '@bliks-io/ui';
import { useExternalContacts } from '@/api/queries/useExternalContacts';
import { useCreateExternalContact } from '@/api/mutations/useExternalContacts';
import { useExternalContactPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { buildEntityPickerCopy, buildSuggestNew } from '../buildCopy';
import { EntityPickerBucketsList } from '../shared/EntityPickerBucketsList';
import {
  buildBucketSections,
  buildRowMetaIndex,
  shouldShowBucketView
} from '../shared/buildBucketSections';

const SEARCH_KEYS: EntityPickerSearchKey<ExternalContact>[] = [
  { name: 'name', weight: 0.5 },
  { name: 'email', weight: 0.2 },
  { name: 'type', weight: 0.15 },
  { name: 'description', weight: 0.15 }
];

export interface UseExternalContactPickerConfigArgs {
  open: boolean;
  mode: 'single' | 'multi';
  processId?: string;
  recordingId?: string;
}

export interface UseExternalContactPickerConfigResult {
  items: ExternalContact[];
  isLoading: boolean;
  config: EntityPickerConfig<ExternalContact>;
  suggestNew: EntityPickerSuggestNew;
  renderSuggestDialog: (args: {
    /** Called with the newly created contact's id after a successful submit. */
    onCreated: (createdId: string) => void;
  }) => React.ReactNode;
}

export function useExternalContactPickerConfig({
  open,
  mode,
  processId,
  recordingId
}: UseExternalContactPickerConfigArgs): UseExternalContactPickerConfigResult {
  const { t } = useTranslation();
  const { data: contacts = [], isLoading } = useExternalContacts();
  const { data: suggestions, isLoading: isLoadingSuggestions } =
    useExternalContactPickerSuggestions({
      processId,
      recordingId,
      enabled: open
    });
  const createMutation = useCreateExternalContact();
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialName, setCreateInitialName] = useState('');

  const contactList = contacts as ExternalContact[];
  const byId = useMemo(() => new Map(contactList.map((c) => [c._id, c] as const)), [contactList]);

  const rowMetaById = useMemo(() => buildRowMetaIndex(suggestions), [suggestions]);

  const config = useMemo<EntityPickerConfig<ExternalContact>>(() => {
    const baseCopy = buildEntityPickerCopy(t, 'externalContact');
    return {
      headerIcon: <Contact className="h-4 w-4" />,
      searchKeys: SEARCH_KEYS,
      matchInField: {
        primaryField: 'name',
        fieldLabels: {
          email: t('entityPicker.common.fields.email'),
          type: t('entityPicker.common.fields.type'),
          description: t('entityPicker.common.fields.description')
        }
      },
      facet: { field: 'type', allLabel: t('entityPicker.common.allFacet') },
      getRowMeta: (item): EntityPickerRowMeta => rowMetaById.get(item._id) ?? {},
      getChipName: (item) => item.name ?? '',
      renderRow: (item, ctx) => (
        <ExternalContactCard
          contact={item as PopulatedExternalContact}
          variant="with-type"
          itemVariant="default"
          size="xs"
          titleSlot={
            <EntityPickerHighlightedText
              text={item.name ?? ''}
              field="name"
              matches={ctx.matches}
            />
          }
        />
      ),
      bucketing: {
        isLoading: isLoadingSuggestions,
        hasBuckets: (candidates) => shouldShowBucketView(suggestions, candidates),
        renderEmptyQueryView: ({ items, selectedIds: draft, toggleId }) => {
          const subset = new Map(items.map((c) => [c._id, c] as const));
          const filteredById = new Map<string, ExternalContact>();
          for (const [id, c] of subset) if (byId.has(id)) filteredById.set(id, c);
          const sections = buildBucketSections<ExternalContact>({
            byId: filteredById,
            suggestions,
            t,
            config: {
              byProcessTitle: (processName) =>
                processName
                  ? `${t('entityPicker.externalContact.buckets.byProcess')} · ${processName}`
                  : t('entityPicker.externalContact.buckets.byProcess'),
              byProcessSubtitle: (sampleSize) =>
                t('entityPicker.system.buckets.byProcessSampleSize', {
                  count: sampleSize
                }),
              byTypeTitle: (type) => t('entityPicker.externalContact.buckets.byType', { type })
            }
          });
          return (
            <EntityPickerBucketsList<ExternalContact>
              sections={sections}
              selectedIds={draft}
              onToggle={toggleId}
              mode={mode}
              renderItem={(item) => (
                <ExternalContactCard
                  contact={item as PopulatedExternalContact}
                  variant="with-type"
                  itemVariant="default"
                  size="xs"
                />
              )}
            />
          );
        }
      },
      copy: baseCopy
    };
  }, [t, suggestions, isLoadingSuggestions, byId, rowMetaById, mode]);

  const suggestNew = buildSuggestNew((q) => {
    setCreateInitialName(q);
    setCreateOpen(true);
  });

  const renderSuggestDialog: UseExternalContactPickerConfigResult['renderSuggestDialog'] = ({
    onCreated
  }) => (
    <ExternalContactFormDialog
      open={createOpen}
      onOpenChange={setCreateOpen}
      onSubmit={async (data) => {
        const result = await createMutation.mutateAsync(data as AddExternalContactRequest);
        setCreateOpen(false);
        if (result?.data?._id) onCreated(result.data._id);
      }}
      isSubmitting={createMutation.isPending}
      existingContacts={contactList as ExternalContact[]}
      initialName={createInitialName}
    />
  );

  return {
    items: contactList,
    isLoading,
    config,
    suggestNew,
    renderSuggestDialog
  };
}
